import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AuthenticatedUser, Permission, SystemRole } from '@repo/contracts';
import { hasPermission, ROLE_PRIORITY } from '@repo/contracts';

import type { RootState } from './store';

interface AuthState {
  user: AuthenticatedUser | null;
}

const initialState: AuthState = { user: null };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    sessionEstablished: (state, action: PayloadAction<AuthenticatedUser>) => {
      state.user = action.payload;
    },
    sessionCleared: (state) => {
      state.user = null;
    },
  },
});

export const { sessionEstablished, sessionCleared } = authSlice.actions;
export const authReducer = authSlice.reducer;

export const selectCurrentUser = (state: RootState): AuthenticatedUser | null => state.auth.user;

/** The token's own `activeRole`: the one role the server authorizes this person as. */
export const selectActiveRole = (state: RootState): SystemRole | null =>
  state.auth.user?.activeRole ?? null;

/** Roles the person actually holds, highest priority first — the "Switch role" list. */
const EMPTY_ROLES: SystemRole[] = [];

// Memoised: a fresh array on every call re-renders every component that reads it.
export const selectHeldRoles = createSelector(
  [(state: RootState) => state.auth.user?.heldRoles],
  (heldRoles): SystemRole[] =>
    heldRoles ? ROLE_PRIORITY.filter((role) => heldRoles.includes(role)) : EMPTY_ROLES,
);

/** Every permission check in the app goes through this, directly or via `selectCan`. */
export const selectPermissions = (state: RootState): Permission[] =>
  state.auth.user?.permissions ?? [];

export const selectCan =
  (...required: Permission[]) =>
  (state: RootState): boolean =>
    hasPermission(selectPermissions(state), required);
